import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import { POSTS, fetchDynamicPosts } from "../blog/posts";
import MarketingShell from "../components/marketing/MarketingShell";

export default function BlogIndex() {
  const [q, setQ] = useState("");
  const [dynamicPosts, setDynamicPosts] = useState([]);

  useEffect(() => {
    document.title = "EcomCatalog AI Blog — AI Listings, Auto Cataloguing & Ecommerce Growth";
    fetchDynamicPosts(24).then(setDynamicPosts).catch(() => setDynamicPosts([]));
  }, []);

  // Merge AI-generated articles (newest first) with the static seed
  // posts. Dedup by slug — static wins on conflict.
  const allPosts = useMemo(() => {
    const seen = new Set(POSTS.map((p) => p.slug));
    const merged = [...POSTS, ...dynamicPosts.filter((d) => !seen.has(d.slug))];
    return merged.sort(
      (a, b) => (b.date || "").localeCompare(a.date || "")
    );
  }, [dynamicPosts]);

  const filtered = useMemo(() => {
    const Q = q.trim().toLowerCase();
    if (!Q) return allPosts;
    return allPosts.filter(
      (p) =>
        p.title.toLowerCase().includes(Q) ||
        (p.description || "").toLowerCase().includes(Q) ||
        (p.keywords || "").toLowerCase().includes(Q)
    );
  }, [q, allPosts]);

  const [featured, ...rest] = filtered;

  return (
    <MarketingShell>
      <section className="pt-16 sm:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            Blog
          </div>
          <h1 className="font-mkt-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter mt-4 leading-[1.02] max-w-4xl">
            Growth playbooks for <span className="mkt-gradient-text-vivid">Indian D2C.</span>
          </h1>
          <p className="text-lg text-slate-400 mt-6 max-w-2xl leading-relaxed">
            Deep-dive guides on AI listings, auto-cataloguing, ad ROAS and quick-commerce
            for sellers on Myntra, Nykaa, Ajio, Amazon, Flipkart, Swiggy &amp; Blinkit.
          </p>

          <div className="mt-10 relative max-w-xl">
            <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-12 pr-4 py-3.5 bg-white/[0.03] border border-white/[0.1] rounded-2xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition placeholder:text-slate-500"
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              className="mkt-glass mkt-hover-lift overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-0 group mb-10"
              data-testid={`blog-featured-${featured.slug}`}
            >
              <div className="aspect-[16/10] lg:aspect-auto lg:min-h-[420px] overflow-hidden bg-black">
                <img
                  src={featured.cover}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                  Featured · {featured.date}
                </div>
                <h2 className="font-mkt-display text-3xl sm:text-4xl font-semibold tracking-tight mt-3 leading-tight group-hover:text-indigo-200 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-slate-400 mt-4 line-clamp-3">{featured.description}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-indigo-300 text-sm font-semibold">
                  Read article <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="mkt-glass mkt-hover-lift overflow-hidden group"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-black">
                  <img
                    src={p.cover}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-2">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={11} /> {p.date}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} /> {p.readMin} min
                    </span>
                  </div>
                  <h2 className="font-mkt-display text-xl font-semibold tracking-tight leading-tight group-hover:text-indigo-200 transition-colors">
                    {p.title}
                  </h2>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.description}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-indigo-300 text-sm font-semibold">
                    Read <ArrowRight size={13} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              No matches for "<span className="text-white">{q}</span>"
            </div>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
